import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, FolderOpen, LayoutTemplate, Rocket, HelpCircle, X, Copy } from 'lucide-react'
import toast from 'react-hot-toast'
import { useProjectStore } from '@/store/projectStore'
import { discardEmptyDraft } from '@/api/generateapp'
import OnboardingHelpChat from './OnboardingHelpChat'

interface NoAppDialogProps {
  open: boolean
  onClose: () => void
  onOpenSidebar: () => void
}

const DISMISS_KEY = 'twasta_no_app_dialog_dismissed'

export function isNoAppDialogDismissed() {
  return localStorage.getItem(DISMISS_KEY) === '1'
}

export default function NoAppDialog({ open, onClose, onOpenSidebar }: NoAppDialogProps) {
  const navigate = useNavigate()
  const { projects, fetchProjects } = useProjectStore()
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)

  if (!open) return null

  const hasApps = projects.length > 0

  const finish = () => {
    if (dontShowAgain) localStorage.setItem(DISMISS_KEY, '1')
    onClose()
  }

  // A listed "Draft Application" is usually just the husk /prepare-draft
  // leaves behind after its staged sources were adopted into a named app —
  // it must not block the user (it survives deleting the app it was staged
  // for). Ask the server: it clears an empty husk and only reports `blocking`
  // when the draft holds real work.
  const handleCreate = async () => {
    const hasDraft = projects.some((p) => p.application.folder_name === 'Draft Application')
    if (hasDraft) {
      try {
        const { blocking, removed } = await discardEmptyDraft()
        if (blocking) {
          toast.error('A draft application already exists. Save or discard it before creating a new one.')
          return
        }
        if (removed) await fetchProjects()
      } catch (err) {
        console.error('discard-empty-draft failed', err)
        toast.error('Could not check the existing draft application. Please try again.')
        return
      }
    }
    finish()
    navigate('/builder')
  }

  const handleOpen = () => {
    finish()
    onOpenSidebar()
  }

  const handleSamples = () => {
    finish()
    navigate('/samples')
  }

  const handleReplicate = () => {
    finish()
    navigate('/create-from-git?tab=source')
  }

  // "I'm a Pro user — take me to Workbench": just dismiss and stay on the
  // (empty) Workbench. No plan change.
  const handleSkip = () => {
    finish()
  }

  // Brand-new users (no apps) see 4 simple options. Returning users also get
  // "Open existing app". Never show "Open existing" when there's nothing to open.
  const cardCount = hasApps ? 5 : 4

  return (
    <div
      className="overlay"
      onClick={(e) => { if (e.target === e.currentTarget) finish() }}
    >
      <div
        className="modal animate-slide-up"
        style={{ maxWidth: 760, width: '95%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 className="modal-title">Get started</h3>
          <button className="modal-close" onClick={finish} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', marginBottom: '1rem' }}>
          Pick how you'd like to begin. You can always come back to these options from the left rail.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cardCount}, 1fr)`,
            gap: '0.75rem',
          }}
        >
          <OptionCard
            icon={<Plus size={28} />}
            title="Create new app"
            description="Describe what you want; AI builds the first version."
            onClick={handleCreate}
            accent="var(--color-accent)"
          />
          <OptionCard
            icon={<LayoutTemplate size={28} />}
            title="Open a sample app"
            description="Start from a ready-made example you can customize."
            onClick={handleSamples}
            accent="#009974"
          />
          <OptionCard
            icon={<Copy size={28} />}
            title="Replicate an existing app"
            description="Point at its source code; AI rebuilds it on Twasta."
            onClick={handleReplicate}
            accent="#d97706"
          />
          {hasApps && (
            <OptionCard
              icon={<FolderOpen size={28} />}
              title="Open existing app"
              description={`You have ${projects.length} app${projects.length === 1 ? '' : 's'}.`}
              onClick={handleOpen}
              accent="#0061D0"
            />
          )}
          <OptionCard
            icon={<Rocket size={28} />}
            title="Look around first"
            description="Skip the intro and explore the Workbench."
            onClick={handleSkip}
            accent="#8b5cf6"
          />
        </div>

        {/* Embedded help — curated FAQ + AI chat about Twasta features. */}
        <div style={{ marginTop: '1rem', borderTop: '1px solid var(--color-border)', paddingTop: '0.875rem' }}>
          <button
            onClick={() => setHelpOpen((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'none',
              border: 'none',
              color: 'var(--color-accent)',
              fontSize: '0.9375rem',
              fontWeight: 600,
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <HelpCircle size={16} />
            {helpOpen ? 'Hide help' : 'Have questions? Ask about Twasta'}
          </button>
          {helpOpen && (
            <div style={{ marginTop: '0.75rem' }}>
              <OnboardingHelpChat />
            </div>
          )}
        </div>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '1.25rem',
            fontSize: '0.875rem',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
          />
          Don't show this again
        </label>
      </div>
    </div>
  )
}

interface OptionCardProps {
  icon: React.ReactNode
  title: string
  description: string
  onClick: () => void
  accent: string
}

function OptionCard({ icon, title, description, onClick, accent }: OptionCardProps) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '0.5rem',
        padding: '1.25rem',
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'all var(--transition-fast)',
        color: 'var(--color-text)',
        minHeight: 160,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = accent
        e.currentTarget.style.transform = 'translateY(-2px)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--color-border)'
        e.currentTarget.style.transform = 'translateY(0)'
      }}
    >
      <div style={{ color: accent }}>{icon}</div>
      <div style={{ fontSize: '1rem', fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
        {description}
      </div>
    </button>
  )
}
